// src/components/Navigation/Navigation.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Briefcase, CalendarPlus, Menu, X } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import './Navigation.css';
import { toggleSidebar, openDemoModal } from '../../store/uiSlice';
import { getManagerOrganizations } from '../../api/organizationApi';
import LogoContainer from './components/LogoContainer/LogoContainer';
import { selectPaymentSystemEnabled } from '../../store/featureSlice';
import { PLAN_CODES, isStarterPlanCode } from '../../utils/billingPlans';

export default function Navigation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useSelector((s) => s.user);
  const paymentSystemEnabled = useSelector(selectPaymentSystemEnabled);

  const [mgrOrgId, setMgrOrgId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const planCode = user?.billing?.plan?.code || '';
  const isStarterPlan = isStarterPlanCode(planCode, user?.billing?.plan?.name);
  const isProPlan = planCode === PLAN_CODES.pro;
  const showUpgradeCta = paymentSystemEnabled && (isStarterPlan || isProPlan);
  const upgradeCtaLabel = isProPlan ? 'Upgrade to Elite' : 'Upgrade';

  useEffect(() => {
    let alive = true;
    if (!user) return;
    (async () => {
      try {
        const { data } = await getManagerOrganizations();
        if (!alive) return;
        setMgrOrgId(Array.isArray(data) && data.length ? data[0].id : null);
      } catch {
        if (alive) setMgrOrgId(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const currentPage =
    location.pathname.startsWith('/subscription') ? 'subscription' :
    location.pathname.startsWith('/profile') ? 'profile' :
    location.pathname.startsWith('/org/') ? 'management' : '';

  const handleNavigate = (page) => {
    switch (page) {
      case 'create': navigate('/conversation-type'); break;
      case 'management': if (mgrOrgId) navigate(`/org/${mgrOrgId}/management?tab=organization`); break;
      case 'subscription': navigate('/subscription'); break;
      default: break;
    }
  };

  // ---------- LOGGED OUT ----------
  if (!user) {
    return (
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-content">
            <div className="nav-logo" onClick={() => navigate('/')}>
            <LogoContainer handleNavigate={handleNavigate} />
            </div>

            {/* Desktop links (hidden on mobile via CSS) */}
            <div className="nav-links">
              <button
                className="nav-link nav-link-button"
                onClick={() => navigate('/business')}
              >
                Business
              </button>
              {paymentSystemEnabled && (
                <button
                  className="nav-link nav-link-button"
                  onClick={() => navigate('/pricing')}
                >
                  Pricing
                </button>
              )}
            </div>

            <div className="nav-buttons">
           
              <button className="signup-button" onClick={() => navigate('/auth')}>
                Sign In
              </button>

              <button className="practice-button" onClick={() => navigate('/auth')}>
                Practice
              </button>

              <button
                    className="watch-demo-button"
                    onClick={() => {
                      setMobileOpen(false);        
                      dispatch(openDemoModal())
                      navigate('/');        
                    }}
                  >
                    Watch Demo
                  </button>
              {/* Mobile menu toggle */}
              <button
                className="mobile-menu-btn"
                aria-label="Open menu"
                aria-controls="mobile-nav-drawer"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(true)}
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>

        {/* Full-screen mobile drawer */}
        {mobileOpen && (
          <div
            className="mobile-drawer-overlay"
            role="dialog"
            aria-modal="true"
            id="mobile-nav-drawer"
          >
            <div className="mobile-drawer-panel">
              <button
                className="mobile-drawer-close"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                <X size={24} />
              </button>

              <nav className="mobile-drawer-nav">
                <button
                  className="mobile-drawer-link"
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/business');
                  }}
                >
                  Business
                </button>
                {paymentSystemEnabled && (
                  <button
                    className="mobile-drawer-link"
                    onClick={() => {
                      setMobileOpen(false);
                      navigate('/pricing');
                    }}
                  >
                    Pricing
                  </button>
                )}
              </nav>

              <div className="mobile-drawer-actions">
                <button
                  className="mobile-primary"
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/auth');
                  }}
                >
                  Practice
                </button>
                <button
                  className="mobile-secondary"
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/auth');
                  }}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    );
  }

  // ---------- LOGGED IN ----------
  return (
    <header className="header-nav animate-fade-in">
      <div className="header-container">
        <div className="header-left">
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="dashboard-sidebar"
            onClick={() => dispatch(toggleSidebar())}
          >
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
          </button>

          <LogoContainer handleNavigate={handleNavigate} />
        </div>

        <div className="header-right">
          <div className="nav-buttons hidden-md">
            {mgrOrgId && (
              <button
                className={`nav-button ${currentPage === 'management' ? 'nav-button-active' : ''}`}
                onClick={() => handleNavigate('management')}
                title="Organization Management"
              >
                <Briefcase className="nav-icon" />
                <span>Management</span>
              </button>
            )}
          </div>

          {showUpgradeCta && (
            <button
              className="upgrade-button"
              onClick={() => navigate('/pricing')}
              title={isProPlan ? 'Upgrade to Elite' : 'Upgrade your plan'}
            >
              <span>{upgradeCtaLabel}</span>
            </button>
          )}

          <button
            className="create-button gradient-primary hover-lift"
            onClick={() => handleNavigate('create')}
            title="Create a new practice conversation"
          >
            <CalendarPlus className="nav-icon" />
            <span>Practice</span>
          </button>
        </div>
      </div>
    </header>
  );
}
